import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PersonaBadge from '../common/PersonaBadge';
import { usePulseData } from '../../hooks/usePulseData';

interface TickerItem {
  id: string;
  icon: string;
  text: string;
  detail?: string;
  personaName?: string;
  personaId?: string;
  url?: string;
  color: string;
}

interface DashboardTickerProps {
  personaSummary?: any;
  workflowSummary?: any;
  artifactorySummary?: any;
  recentEvents?: any[];
}

/** Pure function: build ticker items from summary + event data */
function buildTickerItems(ps: any, wf: any, art: any, events: any[]): TickerItem[] {
  const tickerItems: TickerItem[] = [];

  if (ps) {
    tickerItems.push({
      id: 'personas-total', icon: '\u{1F464}',
      text: `${ps.total} personas`,
      detail: `${ps.forged_count} forged \u00b7 ${ps.active_30d} active`,
      color: 'text-purple-400', url: '/dashboards/personas',
    });
    if (ps.avg_capability_score > 0) {
      tickerItems.push({ id: 'avg-cap', icon: '\u26A1', text: `Avg capability: ${ps.avg_capability_score}/10`, color: 'text-indigo-400' });
    }
    if (ps.suggestion_queue_depth > 0) {
      tickerItems.push({ id: 'suggestions', icon: '\u{1F4EC}', text: `${ps.suggestion_queue_depth} pending suggestions`, color: 'text-amber-400' });
    }
  }

  if (wf) {
    tickerItems.push({
      id: 'workflows-total', icon: '\u{1F500}',
      text: `${wf.total} workflows`,
      detail: `${wf.total_executions} runs \u00b7 ${wf.success_rate}% success`,
      color: 'text-blue-400', url: '/dashboards/workflows',
    });
    if (wf.active_now > 0) {
      tickerItems.push({ id: 'wf-active', icon: '\u{1F7E2}', text: `${wf.active_now} workflow${wf.active_now > 1 ? 's' : ''} running now`, color: 'text-green-400' });
    }
  }

  if (art) {
    tickerItems.push({
      id: 'art-total', icon: '\u{1F4E6}',
      text: `${art.total} projects \u00b7 ${art.total_nodes} nodes`,
      detail: `${art.hydration_rate}% hydrated`,
      color: 'text-cyan-400', url: '/dashboards/artifactory',
    });
    if (art.ghost_count > 0) {
      tickerItems.push({ id: 'art-ghosts', icon: '\u{1F47B}', text: `${art.ghost_count} ghost nodes awaiting hydration`, color: 'text-gray-400' });
    }
    if (art.churn_count > 0) {
      tickerItems.push({ id: 'art-churn', icon: '\u{1F525}', text: `${art.churn_count} nodes with high re-hydration churn`, color: 'text-red-400' });
    }
  }

  // Recent events
  events.forEach((e: any) => {
    const meta = e.event_metadata || e.metadata || {};
    const name = meta.persona_name || meta.workflow_name || meta.project_name || '';
    const catIcons: Record<string, string> = { persona: '\u{1F464}', workflow: '\u{1F500}', artifactory: '\u{1F4E6}', gymnasium: '\u{1F3DF}', pulse: '💓', system: '\u2699' };
    const catColors: Record<string, string> = { persona: 'text-purple-400', workflow: 'text-blue-400', artifactory: 'text-cyan-400', gymnasium: 'text-amber-400', pulse: 'text-indigo-400', system: 'text-gray-400' };
    const cat = e.event_category || e.category || 'system';

    const typeText: Record<string, string> = {
      'persona.published': `${name} published`,
      'persona.genesis_completed': `New persona forged${meta.duration_ms ? ` (${(meta.duration_ms/1000).toFixed(1)}s)` : ''}`,
      'persona.compile_completed': `${name} compiled`,
      'persona.remixed': `${meta.new_persona_name || 'Persona'} remixed`,
      'workflow.execution_started': `"${name}" executing`,
      'workflow.execution_completed': `"${name}" ${meta.status || 'done'}`,
      'workflow.phase_completed': `Phase "${meta.phase_label || '?'}" ${meta.status || 'done'}`,
      'artifactory.node_hydration_completed': `${meta.node_path || 'Node'} hydrated`,
      'artifactory.manifest_created': `Project scaffolded`,
      'gymnasium.match_completed': `Arena: ${meta.winner_name || 'tie'} won`,
      'gymnasium.gauntlet_run_completed': `${name} scored ${meta.pass_rate != null ? (meta.pass_rate * 100).toFixed(0) + '%' : '?'} on "${meta.gauntlet_name || ''}"`,
      'gymnasium.ranking_updated': `${meta.persona_name || ''} Elo ${meta.delta > 0 ? '+' : ''}${meta.delta || 0}`,
      'pulse.heartbeat.generated': 'Daily Heartbeat generated',
    };

    tickerItems.push({
      id: `evt-${e.id}`,
      icon: catIcons[cat] || '\u2728',
      text: typeText[e.event_type] || e.event_type.replace(/\./g, ' '),
      personaName: meta.persona_name,
      personaId: e.target_type === 'persona' ? e.target_id : undefined,
      color: catColors[cat] || 'text-gray-400',
    });
  });

  return tickerItems;
}

const DashboardTicker: React.FC<DashboardTickerProps> = ({
  personaSummary, workflowSummary, artifactorySummary, recentEvents,
}) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState<TickerItem[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const { data: pulseData } = usePulseData();

  // Prepend pulse headline (first sentence of latest briefing) as a pinned ticker item
  const pulseHeadlineItems = useMemo(() => {
    const pulseItems = pulseData?.items ?? [];
    if (pulseItems.length === 0 || !pulseItems[0]?.content_preview) return items;
    const firstSentence = pulseItems[0].content_preview.split('.')[0] + '.';
    return [
      { id: 'pulse-headline', icon: '💓', text: firstSentence, color: 'text-indigo-400', url: '/heartbeat' },
      ...items,
    ];
  }, [items, pulseData]);

  const hasExternalData = recentEvents !== undefined
    && personaSummary !== undefined
    && workflowSummary !== undefined
    && artifactorySummary !== undefined;

  // When parent provides data, use it directly — no fetch, no poll
  useEffect(() => {
    if (hasExternalData) {
      setItems(buildTickerItems(personaSummary, workflowSummary, artifactorySummary, recentEvents!));
    }
  }, [hasExternalData, personaSummary, workflowSummary, artifactorySummary, recentEvents]);

  // When no external data, fetch + poll as before (backward compat for DashboardLayout etc.)
  useEffect(() => {
    if (hasExternalData) return;
    let cancelled = false;

    const load = async () => {
      try {
        const [psResp, wfResp, artResp, evtResp] = await Promise.all([
          api.get('/analytics/cross-system/persona/summary'),
          api.get('/analytics/cross-system/workflow/summary'),
          api.get('/analytics/cross-system/artifactory/summary'),
          api.get('/analytics/cross-system/events?limit=8'),
        ]);
        if (!cancelled) {
          setItems(buildTickerItems(psResp.data, wfResp.data, artResp.data, evtResp.data?.events || []));
        }
      } catch (err) {
        if (!cancelled) console.error('Ticker load error:', err);
      }
    };

    load();
    const interval = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [hasExternalData]);

  // Auto-scroll animation
  useEffect(() => {
    if (!scrollRef.current || items.length === 0 || isPaused) return;
    const el = scrollRef.current;
    let animFrame: number;
    let scrollPos = 0;

    const tick = () => {
      scrollPos += 0.5;
      if (scrollPos >= el.scrollWidth / 2) scrollPos = 0;
      el.scrollLeft = scrollPos;
      animFrame = requestAnimationFrame(tick);
    };

    animFrame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrame);
  }, [items, isPaused]);

  if (pulseHeadlineItems.length === 0) return null;

  // Duplicate items for seamless scroll loop
  const displayItems = [...pulseHeadlineItems, ...pulseHeadlineItems];

  return (
    <div
      className={`relative overflow-hidden mb-4 rounded-xl border ${
        darkMode
          ? 'bg-gradient-to-r from-gray-900/80 via-gray-800/60 to-gray-900/80 border-gray-700/40'
          : 'bg-gradient-to-r from-gray-50 via-white to-gray-50 border-gray-200'
      }`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Fade edges */}
      <div className={`absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none ${
        darkMode ? 'bg-gradient-to-r from-gray-900 to-transparent' : 'bg-gradient-to-r from-gray-50 to-transparent'
      }`} />
      <div className={`absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none ${
        darkMode ? 'bg-gradient-to-l from-gray-900 to-transparent' : 'bg-gradient-to-l from-gray-50 to-transparent'
      }`} />

      <div
        ref={scrollRef}
        className="flex gap-6 px-6 py-2.5 overflow-hidden whitespace-nowrap"
      >
        {displayItems.map((item, i) => (
          <div
            key={`${item.id}-${i}`}
            className={`inline-flex items-center gap-2 shrink-0 cursor-pointer transition-opacity ${
              isPaused ? 'opacity-100' : 'opacity-80 hover:opacity-100'
            }`}
            onClick={() => item.url && navigate(item.url)}
          >
            <span className="text-sm">{item.icon}</span>
            <span className={`text-xs font-medium ${item.color}`}>{item.text}</span>
            {item.detail && (
              <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{item.detail}</span>
            )}
            {item.personaName && (
              <PersonaBadge personaId={item.personaId || ''} personaName={item.personaName} size="xs" clickable={false} />
            )}
            <span className={`mx-2 ${darkMode ? 'text-gray-700' : 'text-gray-300'}`}>{'\u00b7'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardTicker;
